import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Mail, Phone, User as UserIcon, FileText, MapPin, Package,
  History, Bell, Printer, Receipt, CreditCard, Send, AlertCircle, ChevronRight, Info,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useMyAnagrafica } from '../../hooks/useMyAnagrafica';
import { BudgetCard } from '../../components/portal/BudgetCard';
import { useDashboardCounters, type DashboardCounters } from '../../hooks/useDashboardCounters';
import { useContatto } from '../../hooks/useContatto';
import { richiedeCliente } from '../../utils/contatto';
import { CondizioniTrasporto } from '../../components/portal/CondizioniTrasporto';
import { condizioniCliente } from '../../utils/condizioniTrasporto';

const SHORTCUTS: Array<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  to: string;
  badge?: keyof DashboardCounters;
}> = [
  { icon: Package, label: 'I miei ordini', to: '/area-clienti/ordini', badge: 'ordiniInLavorazione' },
  { icon: FileText, label: 'Documenti', to: '/area-clienti/documenti' },
  { icon: Mail, label: 'Messaggi', to: '/area-clienti/avvisi', badge: 'messaggiNonLetti' },
  { icon: History, label: 'Storico acquisti', to: '/area-clienti/storico-acquisti' },
  { icon: Printer, label: 'I miei modelli', to: '/area-clienti/parco-macchine' },
  { icon: MapPin, label: 'Indirizzi spedizione', to: '/area-clienti/indirizzi-spedizione' },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const { data: ana, loading, error } = useMyAnagrafica();
  // Contatto: account senza Cliente su NTS. Restano i blocchi che non lo
  // chiedono; gli altri tornano dopo il primo ordine.
  const contatto = useContatto();
  const { data: counters } = useDashboardCounters(!contatto);
  const shortcuts = useMemo(() => SHORTCUTS.filter(s => !(contatto && richiedeCliente(s.to))), [contatto]);

  // RF-HOME-05: selettore destinazione (preseleziona la destinazione predefinita)
  const destinazioni = useMemo(() => ana?.destinazioni ?? [], [ana]);
  const destPref = ana?.destinazionePreferita ?? null;
  const [destPick, setDestPick] = useState<number | ''>('');
  const [destPickTouched, setDestPickTouched] = useState(false);

  // Sincronizza il dropdown con la destinazione predefinita non appena
  // l'anagrafica è caricata, finché l'utente non seleziona manualmente.
  useEffect(() => {
    if (destPickTouched) return;
    if (destPref != null && destinazioni.some(d => d.dd_coddest === destPref)) {
      setDestPick(destPref);
    } else if (destinazioni.length > 0 && destPick === '') {
      setDestPick(destinazioni[0].dd_coddest ?? '');
    }
  }, [destPref, destinazioni, destPickTouched, destPick]);

  const destSelected = useMemo(
    () => destinazioni.find(d => d.dd_coddest === destPick),
    [destinazioni, destPick],
  );

  return (
    <div data-testid="customer-dashboard" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Benvenuto, {ana?.ragioneSociale ?? user?.nome ?? 'Cliente'}</h1>
        <p className="text-sm text-muted-foreground mt-1">Gestisci ordini, documenti, indirizzi e modelli dal tuo pannello personale.</p>
      </div>

      {contatto && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 flex items-start gap-2" data-testid="home-contatto-nota">
          <Info className="w-4 h-4 flex-none mt-0.5" />
          <span>
            Il tuo account cliente DTK viene aperto con il primo ordine: da lì trovi qui ordini, documenti e indirizzi
            di spedizione. Intanto puoi consultare il <Link to="/prodotti/toner" className="underline font-medium">catalogo</Link> con
            i tuoi prezzi, salvare i preferiti e ordinare.
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> Impossibile caricare l'anagrafica: {error}
        </div>
      )}

      {/* Riga 1: identità cliente + assistente commerciale */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* RF-HOME-01: identità */}
        <section className="rounded-xl border bg-white p-5" data-testid="home-card-identity">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(210,40%,93%)] flex items-center justify-center flex-none">
              <Building2 className="w-5 h-5 text-[hsl(214,58%,41%)]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Dati cliente</div>
              {loading ? (
                <SkeletonLines n={3} />
              ) : (
                <>
                  <div className="text-base font-semibold mt-1 truncate">{ana?.ragioneSociale ?? '—'}</div>
                  <div className="text-xs font-mono text-muted-foreground mt-0.5" data-testid="home-cod-cliente">
                    Cod. cliente: {ana?.an_conto ?? (contatto ? 'assegnato al primo ordine' : '—')}
                  </div>
                  {ana?.email && (
                    <div className="text-xs text-muted-foreground mt-1 truncate flex items-center gap-1.5">
                      <Mail className="w-3 h-3" /> {ana.email}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* RF-HOME-02: assistente commerciale */}
        <section className="rounded-xl border bg-white p-5" data-testid="home-card-agente">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-none">
              <UserIcon className="w-5 h-5 text-amber-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assistente commerciale</div>
              {loading ? <SkeletonLines n={3} /> : (() => {
                const agenteNome = ana?.agente
                  ? [ana.agente.nome, ana.agente.cognome].filter(Boolean).join(' ') || ana.agente.ragioneSociale
                  : (ana?.agenteInternoNome || ana?.agenteEsternoNome);
                const agenteEmail = ana?.agente?.email || ana?.agenteInternoEmail || ana?.agenteEsternoEmail;
                const agenteTel = ana?.agente?.telefono || ana?.agente?.cellulare || ana?.agenteInternoTelefono || ana?.agenteEsternoTelefono;
                if (!agenteNome) {
                  return (
                    <div className="text-sm text-muted-foreground mt-1">
                      Nessun agente assegnato. Contatta <a href="mailto:info@dtk.it" className="text-[hsl(210,64%,29%)] underline">info@dtk.it</a> per assistenza.
                    </div>
                  );
                }
                return (
                  <>
                    <div className="text-base font-semibold mt-1 truncate">{agenteNome}</div>
                    {agenteEmail && (
                      <div className="text-xs text-muted-foreground mt-1 truncate flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> {agenteEmail}
                      </div>
                    )}
                    {agenteTel && (
                      <div className="text-xs text-muted-foreground mt-0.5 truncate flex items-center gap-1.5">
                        <Phone className="w-3 h-3" /> {agenteTel}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </section>
      </div>

      {/* RF-HOME-03: scorciatoie.
          Con il drawer che si occupa della navigazione, queste non sono
          più una copia del menu ma il cruscotto della dashboard: 2 colonne
          su mobile invece di 3, label leggibili invece di 11px, e i
          contatori reali. Etichette e icone allineate a `accountNav`
          (prima "Documenti" aveva l'icona Truck e "Messaggi" si chiamava
          "Avvisi" solo qui). */}
      <section data-testid="home-shortcuts">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Scorciatoie</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {shortcuts.map(s => {
            const count = s.badge ? counters[s.badge] : 0;
            return (
              <Link
                key={s.label}
                to={s.to}
                data-testid={`shortcut-${s.label.toLowerCase().replace(/\s/g, '-').replace(/[&]/g, '')}`}
                className="relative rounded-xl border bg-white p-3 min-h-[88px] hover:border-[hsl(210,64%,29%)] hover:shadow-sm transition-all flex flex-col items-center justify-center gap-2 text-center"
              >
                <s.icon className="w-6 h-6 text-[hsl(210,64%,29%)]" />
                <span className="text-[15px] lg:text-[11px] font-medium leading-tight">{s.label}</span>
                {count > 0 && (
                  <span
                    data-testid={`shortcut-badge-${s.badge}`}
                    className="absolute top-2 right-2 inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold text-white"
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </section>

      {/* RF-HOME-budget: soglia fatturato / sconto premio (legato al conto) */}
      {!contatto && <BudgetCard />}

      {/* Riga 3: fatturazione + spedizione */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* RF-HOME-04: indirizzo di fatturazione */}
        <section className="rounded-xl border bg-white p-5" data-testid="home-card-billing">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="w-4 h-4 text-[hsl(210,64%,29%)]" />
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex-1">Indirizzo di fatturazione</div>
            <Link to="/area-clienti/dati-fatturazione" className="tap text-[13px] lg:text-[11px] text-[hsl(210,64%,29%)] hover:underline flex items-center justify-end gap-0.5">
              dettagli <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          {loading ? <SkeletonLines n={5} /> : (
            <div className="space-y-1.5 text-sm">
              <div className="font-semibold">{ana?.ragioneSociale ?? '—'}</div>
              {ana?.indirizzo && <div className="text-muted-foreground">{ana.indirizzo}</div>}
              {(ana?.cap || ana?.citta) && <div className="text-muted-foreground">{[ana.cap, ana.citta, ana.provincia && `(${ana.provincia})`].filter(Boolean).join(' ')}</div>}
              <Field label="P.IVA" value={ana?.partitaIva} mono />
              <Field label="C.F." value={ana?.codiceFiscale} mono />
              <Field label="SDI" value={ana?.sdiCode} mono />
              {ana?.pec && <Field label="PEC" value={ana.pec} />}
              <Field label="Email" value={ana?.email} />
              <Field label="Tel." value={ana?.telefono} />
            </div>
          )}
        </section>

        {/* RF-HOME-05: indirizzo di spedizione con selettore */}
        <section className="rounded-xl border bg-white p-5" data-testid="home-card-shipping">
          <div className="flex items-center gap-2 mb-3">
            <Send className="w-4 h-4 text-[hsl(210,64%,29%)]" />
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex-1">Indirizzo di spedizione</div>
            {!contatto && (
              <Link to="/area-clienti/indirizzi-spedizione" className="tap text-[13px] lg:text-[11px] text-[hsl(210,64%,29%)] hover:underline flex items-center justify-end gap-0.5">
                gestisci <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </div>
          {loading ? <SkeletonLines n={4} /> : destinazioni.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Nessun indirizzo aggiuntivo. Spedizione di default presso sede legale.
              {contatto && ' Un altro indirizzo si aggiunge al checkout.'}
            </div>
          ) : (
            <>
              <select
                value={destPick}
                onChange={(e) => { setDestPickTouched(true); setDestPick(e.target.value === '' ? '' : Number(e.target.value)); }}
                data-testid="ship-dest-picker"
                className="w-full mb-3 px-3 py-2 border rounded-lg text-sm bg-white"
              >
                <option value="">— Seleziona destinazione —</option>
                {destinazioni.map(d => (
                  <option key={d.dd_coddest} value={d.dd_coddest ?? ''}>
                    {d.dd_nomdest ?? `Destinazione ${d.dd_coddest}`}{d.dd_coddest === destPref ? ' (predefinita)' : ''}
                  </option>
                ))}
              </select>
              {destSelected ? (
                <div className="text-sm space-y-1">
                  <div className="font-semibold">{destSelected.dd_nomdest}</div>
                  {destSelected.inddest && <div className="text-muted-foreground">{destSelected.inddest}</div>}
                  <div className="text-muted-foreground">
                    {[destSelected.cap, destSelected.citta, destSelected.prov && `(${destSelected.prov})`].filter(Boolean).join(' ')}
                  </div>
                  {destSelected.telef && <Field label="Tel." value={destSelected.telef} />}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic">Seleziona una destinazione per vedere i dettagli.</div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Riga 4: condizioni commerciali + newsletter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* RF-HOME-06: le condizioni sono quelle del Cliente su NTS, che il
            Contatto non ha ancora. */}
        {!contatto && (
        <section className="rounded-xl border bg-white p-5" data-testid="home-card-modes">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Condizioni commerciali</div>
          {loading ? <SkeletonLines n={3} /> : (
            <div className="space-y-2.5 text-sm">
              {/* Trasporto a cura del; Porto e Vettore solo per il Porto Assegnato */}
              <CondizioniTrasporto dati={condizioniCliente(ana)} variante="riquadro" />
              <div className="flex items-center gap-2.5">
                <CreditCard className="w-4 h-4 text-[hsl(210,64%,29%)] flex-none" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pagamento</div>
                  <div className="font-medium">{ana?.codicePagamentoLabel ?? ana?.codpagLabel ?? '—'}</div>
                </div>
              </div>
              {ana?.listinoLabel && (
                <div className="flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-[hsl(210,64%,29%)] flex-none" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Listino assegnato</div>
                    <div className="font-medium">{ana.listinoLabel}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* RF-HOME-07: iscrizione newsletter */}
        <section className="rounded-xl border bg-gradient-to-br from-[hsl(210,40%,98%)] to-[hsl(210,60%,93%)] p-5" data-testid="home-card-newsletter">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-none">
              <Send className="w-5 h-5 text-[hsl(210,64%,29%)]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-[hsl(210,64%,29%)] uppercase tracking-wider">Newsletter DTK</div>
              <div className="font-semibold mt-1">Resta aggiornato</div>
              <div className="text-xs text-muted-foreground mt-1">
                Promozioni, novità di catalogo e disponibilità outlet direttamente nella tua casella.
              </div>
              <Link
                to="/area-clienti/preferenze"
                data-testid="home-newsletter-cta"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[hsl(210,64%,29%)] text-white text-xs font-semibold hover:bg-[hsl(210,64%,24%)]"
              >
                Gestisci iscrizione <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value?: string | number | null; mono?: boolean }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <span className="font-semibold text-[10px] uppercase tracking-wider min-w-[40px]">{label}:</span>
      <span className={mono ? 'font-mono' : ''}>{value}</span>
    </div>
  );
}

function SkeletonLines({ n }: { n: number }) {
  return (
    <div className="space-y-2 mt-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="h-3 bg-[hsl(0,0%,93%)] rounded animate-pulse" style={{ width: `${50 + (i * 11) % 50}%` }} />
      ))}
    </div>
  );
}
